import {
    SofaFormControlDescriptionWithGtm,
    SofaFormValidator,
    checkboxRequiredValidator,
    minLengthValidator,
    regexpValidator,
    stringRequiredValidator
} from '@sofology/sofologycomponents';

import {
    NumberPropertyDescription,
    PropertyDescription,
    PropertyDescriptionTypes,
    SelectPropertyDescription,
    SofaFormCrossValidationHandlerType,
    StringPropertyDescription,
    ValidateTypeDescription,
    ValidatorTypeDescription
} from 'models';
import { getErrorMessage } from './getErrorMessage';
import {
    compareNumbersByOperatorValidator,
    maxValueValidator,
    minValueValidator,
    numberRequiredIfValidator,
    numberRequiredValidator
} from './numberValidators';
import {
    conditionStringsValidator,
    compareStringsByOperatorValidator,
    stringRequiredIfValidator,
    bankDataValidator
} from './stringValidators';

import { DataAccessObjectType, SHOULD_BE_RENDERED_FUNC_NAME } from './formHelperAdditionalData';
import { booleanRequiredValidator } from './booleanValidators';

export const getBooleanControlValidators = (property: PropertyDescriptionTypes, dataAccessObject: DataAccessObjectType): SofaFormValidator<boolean>[] => {
    let validators: SofaFormValidator<boolean>[] = [];

    if (property.validate) {
        validators.push(...getValidateForBooleanControl(property, dataAccessObject));
    }

    if (property.required) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Required, property.label);
        validators = [checkboxRequiredValidator(errorMessage)];
    }

    return validators;
};

export const getNumberControlValidators = (property: NumberPropertyDescription, dataAccessObject: DataAccessObjectType): SofaFormValidator<number | null>[] => {
    let validators: SofaFormValidator<number | null>[] = [];

    if (property.validate) {
        validators.push(...getValidateForNumberControl(property, dataAccessObject));
    }

    if (property.required) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Required, property.label);
        validators.push(numberRequiredValidator(errorMessage));
    }

    if (property.min !== undefined) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Min, property.label, property.min);

        validators.push(minValueValidator(errorMessage, property.min));
    }

    if (property.max !== undefined) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Max, property.label, property.max);

        validators.push(maxValueValidator(errorMessage, property.max));
    }

    return validators;
};

export const getStringControlValidators = (property: StringPropertyDescription | SelectPropertyDescription, dataAccessObject: DataAccessObjectType): SofaFormValidator<string>[] => {
    let validators: SofaFormValidator<string>[] = [];
    if (property.required) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Required, property.label);
        validators.push(stringRequiredValidator(errorMessage));
    }

    if (property.validate) {
        validators.push(...getValidateForStringControl(property, dataAccessObject));
    }

    property = property as StringPropertyDescription;

    if (property.minLength !== undefined) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.MinLength, property.label, property.minLength);

        validators.push(minLengthValidator(errorMessage, property.minLength));
    }

    if (property.pattern) {
        let errorMessage = getErrorMessage(ValidatorTypeDescription.Regexp, property.label);

        validators.push(regexpValidator(errorMessage, new RegExp(property.pattern)));
    }

    return validators;
};

export const getFormCrossValidationHandler = (
    getProperty: (controlName: string) => PropertyDescriptionTypes,
    getControlNameByPropertyName: (propertyName: string) => string,
    formDescription: Record<string, SofaFormControlDescriptionWithGtm<any>>,
    formName: string): SofaFormCrossValidationHandlerType => {

    let controlDependencyFunctions: any[] = [];
    const formPropertiesNames = Object.keys(formDescription).map(controlName => getProperty(controlName).name);

    for (let controlName in formDescription) {
        let property = getProperty(controlName);

        let dependencies = getControlDependenciesOnTheSameForm(property, formPropertiesNames);

        dependencies.forEach(dependency => {
            let dependencyControlName = getControlNameByPropertyName(dependency);
            controlDependencyFunctions.push((prevFormState: any, currentFormState: any) => {
                const valueChanged = prevFormState[formName].controls[dependencyControlName].value !== currentFormState[formName].controls[dependencyControlName].value;
                const shouldBeRendered = currentFormState[formName].controls[controlName][SHOULD_BE_RENDERED_FUNC_NAME] ? currentFormState[formName].controls[controlName][SHOULD_BE_RENDERED_FUNC_NAME]() : true;
                if (valueChanged && currentFormState[formName].controls[controlName].isBlurHandled && shouldBeRendered) {
                    currentFormState[formName].controls[controlName].validate();
                }
            });
        });
    }

    return (prevState: any, currentState: any) => {
        controlDependencyFunctions.forEach(func => func(prevState, currentState));
    };
};

function getValidateForBooleanControl(property: PropertyDescriptionTypes, dataAccessObject: DataAccessObjectType): SofaFormValidator<boolean>[] {
    let validators: SofaFormValidator<boolean>[] = [];

    property.validate?.forEach(x => {
        switch (x.type) {
            case ValidateTypeDescription.RequiredIf:
                validators.push(booleanRequiredValidator(x.message, () => dataAccessObject[x.field] as () => boolean, x.value as boolean, x.operator));
                break;
            default:
                break;
        }
    });

    return validators;
}

function getValidateForNumberControl(property: PropertyDescriptionTypes, dataAccessObject: DataAccessObjectType): SofaFormValidator<number | null>[] {
    let validators: SofaFormValidator<number | null>[] = [];

    property.validate?.forEach(x => {
        switch (x.type) {
            case ValidateTypeDescription.Comparator:
                validators.push(compareNumbersByOperatorValidator(x.message, () => dataAccessObject[x.field] as () => number | null, x.operator));
                break;
            case ValidateTypeDescription.RequiredIf:
                validators.push(numberRequiredIfValidator(x.message, () => dataAccessObject[x.field] as () => number | null, x.value as number, x.operator));
                break;
            default:
                break;
        }
    });

    return validators;
}

function getValidateForStringControl(property: PropertyDescriptionTypes, dataAccessObject: DataAccessObjectType): SofaFormValidator<string>[] {
    let validators: SofaFormValidator<string>[] = [];

    property.validate?.forEach(x => {
        switch (x.type) {
            case ValidateTypeDescription.Comparator:
                validators.push(compareStringsByOperatorValidator(x.message, () => dataAccessObject[x.field] as () => string, x.operator));
                break;
            case ValidateTypeDescription.RequiredIf:
                validators.push(stringRequiredIfValidator(x.message, () => dataAccessObject[x.field] as () => string, x.value as string, x.operator));
                break;
            case ValidateTypeDescription.Condition:
                validators.push(conditionStringsValidator(x));
                break;
            case ValidateTypeDescription.BankData:
                validators.push(bankDataValidator(x.message, () => dataAccessObject[x.field] as () => string));
                break;
            default:
                break;
        }
    });

    return validators;
}

function getControlDependenciesOnTheSameForm(property: PropertyDescription, formProperties: string[]): string[] {
    let dependencies: string[] = [];

    if (!property.validate) {
        return dependencies;
    }

    property.validate.forEach(validateDescription => {
        if (validateDescription.field !== property.name && formProperties.includes(validateDescription.field)) {
            dependencies.push(validateDescription.field);
        }
    });

    return dependencies;
}